import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Divider,
  Chip,
  Paper,
} from '@mui/material';
import {
  Download as DownloadIcon,
  CheckCircle as CheckIcon,
  Description as FileIcon,
  Code as CodeIcon,
  Image as ImageIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

const EXPORT_FORMATS = [
  {
    id: 'dxf-clo3d',
    name: 'DXF for CLO3D',
    description: 'CLO3D-compatible DXF with separate pattern pieces',
    icon: <FileIcon />,
    fileExtension: '.dxf',
    color: '#2196f3',
    forCLO3D: true,
    recommended: true,
  },
  {
    id: 'svg',
    name: 'SVG Pattern',
    description: 'Scalable Vector Graphics format for pattern pieces',
    icon: <ImageIcon />,
    fileExtension: '.svg',
    color: '#ff9800',
    forCLO3D: true,
  },
  {
    id: 'yaml',
    name: 'Merged YAML',
    description: 'Complete DNA specification file',
    icon: <CodeIcon />,
    fileExtension: '.yaml',
    color: '#4caf50',
    forCLO3D: false,
  },
  {
    id: 'json',
    name: 'JSON Tech Pack',
    description: 'SIVAD_GARMENT_01 format for manufacturing',
    icon: <CodeIcon />,
    fileExtension: '.json',
    color: '#9c27b0',
    forCLO3D: false,
  },
];

const PatternExport = ({ patternBlock, mergedYAML, generatedSVG, generatedPieces = [], brandName }) => {
  const [downloading, setDownloading] = useState(null);
  const isMultiPiece = generatedPieces && generatedPieces.length > 1;

  const handleDownload = async (format) => {
    try {
      setDownloading(format.id);
      toast.loading(`Preparing ${format.name}...`, { id: 'download' });

      // Simulate download preparation
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // TODO: Implement actual file download from backend
      // For now, create blob downloads for available formats

      let blob;
      let filename = `${brandName || 'pattern'}_${patternBlock?.name || 'design'}${format.fileExtension}`;

      switch (format.id) {
        case 'svg':
          if (isMultiPiece) {
            // Download each piece as its own SVG
            for (const piece of generatedPieces) {
              const pieceBlob = new Blob([piece.svg], { type: 'image/svg+xml' });
              const pieceUrl = URL.createObjectURL(pieceBlob);
              const a = document.createElement('a');
              a.href = pieceUrl;
              a.download = `${brandName || 'pattern'}_${patternBlock?.name || 'design'}_${piece.id || piece.name}${format.fileExtension}`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(pieceUrl);
              await new Promise(r => setTimeout(r, 150));
            }
            toast.success(`${generatedPieces.length} SVG pieces downloaded!`, { id: 'download' });
            setDownloading(null);
            return;
          }
          blob = new Blob([generatedSVG], { type: 'image/svg+xml' });
          break;
        case 'yaml':
          blob = new Blob([mergedYAML], { type: 'text/yaml' });
          break;
        case 'json':
          const jsonData = {
            format: 'SIVAD_GARMENT_01',
            version: '1.0.0',
            pattern: patternBlock?.name,
            brand: brandName,
            generatedAt: new Date().toISOString(),
            yaml: mergedYAML,
            pieces: isMultiPiece
              ? generatedPieces.map(p => ({ id: p.id, name: p.name, cutQuantity: p.cutQuantity, onFold: p.onFold }))
              : [{ id: 'main', name: patternBlock?.name }],
            svg: isMultiPiece ? null : generatedSVG,
          };
          blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
          break;
        case 'dxf-clo3d':
          // CLO3D-compatible DXF export via backend
          const token = localStorage.getItem('token');
          const res = await fetch('/api/spif/export/dxf', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              patternBlock,
              yaml: mergedYAML || { measurements: patternBlock?.measurements || {} },
              format: 'CLO3D',
              includeNotches: true,
              includeGrainline: true,
              includeText: true,
              notchSize: 3,
              units: 'Centimeters'  // Pattern values are in cm
            })
          });

          if (!res.ok) throw new Error('DXF backend error');

          blob = await res.blob();
          break;
        default:
          toast.error(`Format ${format.name} not yet implemented`, { id: 'download' });
          return;
      }

      // Trigger download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`${format.name} downloaded!`, { id: 'download' });
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed', { id: 'download' });
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadAll = async () => {
    toast.loading('Preparing all files...', { id: 'download-all' });

    // Download available formats sequentially
    for (const format of EXPORT_FORMATS.filter(f => f.id !== 'dxf')) {
      await handleDownload(format);
      await new Promise(resolve => setTimeout(resolve, 500)); // Small delay between downloads
    }

    toast.success('All files downloaded!', { id: 'download-all' });
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Export for CLO3D
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Download pattern files in various formats for use in CLO3D and other manufacturing tools.
      </Typography>

      {/* Export Success Alert */}
      <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          {isMultiPiece
            ? `Pattern generation complete! ${generatedPieces.length} pieces ready for export.`
            : 'Pattern generation complete! Your files are ready for export.'}
        </Typography>
      </Alert>

      {/* Download All Button */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<FolderIcon />}
          onClick={handleDownloadAll}
          fullWidth
        >
          {isMultiPiece
            ? `Download All Files (${generatedPieces.length} pieces)`
            : 'Download All Files'}
        </Button>
      </Box>

      <Divider sx={{ my: 3 }}>
        <Chip label="OR DOWNLOAD INDIVIDUALLY" size="small" />
      </Divider>

      {/* Individual Format Downloads */}
      <Grid container spacing={2}>
        {EXPORT_FORMATS.map((format) => (
          <Grid item xs={12} sm={6} key={format.id}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                borderColor: format.forCLO3D ? 'primary.main' : 'divider',
                borderWidth: format.forCLO3D ? 2 : 1,
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ color: format.color }}>
                      {format.icon}
                    </Box>
                    <Typography variant="h6" fontWeight={600}>
                      {format.name}
                    </Typography>
                  </Box>
                  {format.forCLO3D && (
                    <Chip label="CLO3D" size="small" color="primary" />
                  )}
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {format.description}
                </Typography>

                <Button
                  variant={format.forCLO3D ? 'contained' : 'outlined'}
                  fullWidth
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownload(format)}
                  disabled={downloading === format.id}
                >
                  {downloading === format.id ? 'Downloading...' : `Download ${format.fileExtension}`}
                </Button>

              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Next Steps */}
      <Paper variant="outlined" sx={{ mt: 3, p: 2, bgcolor: 'primary.50' }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Next Steps: Import to CLO3D
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Typography variant="body2" fontWeight={600}>1.</Typography>
            </ListItemIcon>
            <ListItemText primary="Open CLO3D and create a new project" />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Typography variant="body2" fontWeight={600}>2.</Typography>
            </ListItemIcon>
            <ListItemText primary="Import the SVG or DXF pattern file" />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Typography variant="body2" fontWeight={600}>3.</Typography>
            </ListItemIcon>
            <ListItemText primary="Create 3D rendering and generate tech pack in CLO3D" />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Typography variant="body2" fontWeight={600}>4.</Typography>
            </ListItemIcon>
            <ListItemText primary="Export CLO3D tech pack and convert to SIVAD_GARMENT_01 YAML format" />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Typography variant="body2" fontWeight={600}>5.</Typography>
            </ListItemIcon>
            <ListItemText primary="Create RFQ and send to manufacturing partners" />
          </ListItem>
        </List>
      </Paper>
    </Box>
  );
};

export default PatternExport;
